function formatCurrency(value) {
    if (value === undefined || value === null || value === '') return '';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return String(value);
    return parsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function pickFirst(...values) {
    return values.find(value => value !== undefined && value !== null && String(value).trim() !== '');
}

function escapeHtml(text) {
    return String(text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function listItemHtml(label, value) {
    const price = value != null && value !== '' ? ` — ${formatCurrency(value)}` : '';
    return `<p>• ${escapeHtml(label)}${escapeHtml(price)}</p>`;
}

function buildPriceTableListHtml(rows) {
    const list = Array.isArray(rows) ? [...rows] : [];
    list.sort((a, b) => Number(a?.numberLife) - Number(b?.numberLife));

    return list
        .map(row => {
            const lives = Number(row?.numberLife);
            if (!Number.isFinite(lives)) return '';
            return listItemHtml(`Até ${lives} vidas`, row?.price);
        })
        .filter(Boolean)
        .join('\n');
}

/**
 * Monta HTML de listas a partir de customização e benefícios da proposta.
 */
function buildContractLists(contract) {
    const customizationProducts = contract?.customizationOrder?.Order_products || [];
    const customLines = customizationProducts
        .map(op => {
            const name = op.Product?.name || op.name;
            const price = pickFirst(op.total, op.subtotal, op.Product?.price);
            return name ? listItemHtml(name, price) : '';
        })
        .filter(Boolean);

    const resale = (contract?.resaleProducts || []).filter(p => !p.deletedAt && !p.disabled);
    const benefitLines = resale
        .map(p => {
            const name = p.name;
            const price = p.price;
            return name ? listItemHtml(name, price) : '';
        })
        .filter(Boolean);

    // Fallback: customProducts no contractData (legado)
    if (!customLines.length && Array.isArray(contract?.contractData?.customProducts)) {
        for (const item of contract.contractData.customProducts) {
            if (typeof item === 'string') customLines.push(listItemHtml(item));
            else if (item?.name) customLines.push(listItemHtml(item.name, item.price));
        }
    }

    const hasAppCustomization = customLines.length > 0 || Boolean(contract?.customizationOrderId);
    const hasAdditionalBenefits = benefitLines.length > 0;

    // Adesão / setup: total da order de customização, se houver
    const adesao = pickFirst(contract?.customizationOrder?.total);

    const priceTableRows =
        contract?.plan?.planPriceTables ||
        contract?.planPriceTables ||
        contract?.contractData?.plan?.planPriceTables ||
        [];
    const priceTableLines = buildPriceTableListHtml(priceTableRows);

    return {
        LISTA_CUSTOMIZACOES: customLines.join('\n'),
        LISTA_BENEFICIOS_ADICIONAIS: benefitLines.join('\n'),
        LISTA_FAIXAS_PRECO: priceTableLines,
        TEM_CUSTOMIZACAO_APP: hasAppCustomization ? '1' : '',
        TEM_BENEFICIOS_ADICIONAIS: hasAdditionalBenefits ? '1' : '',
        TEM_FAIXAS_PRECO: priceTableLines ? '1' : '',
        VALOR_ADESAO: adesao != null && Number(adesao) > 0 ? formatCurrency(adesao) : '',
    };
}

/**
 * Mapa de variáveis do texto do contrato a partir dos dados da proposta.
 * Usa colunas do contract (plano/fidelidade) + contractData.contract (termo).
 */
export function buildContractPlaceholderMap(contract) {
    const contractor = contract?.contractData?.contract || {};
    const club = contract?.Club || {};
    const plan = contract?.plan || contract?.contractData?.plan || {};

    const fidelityMonths = pickFirst(
        contract?.fidelityMonths,
        contract?.contractData?.fidelity?.months,
    );
    const fidelityPenalty = pickFirst(
        contract?.fidelityPenaltyValue,
        contract?.contractData?.fidelity?.penaltyValue,
    );

    const companyName = pickFirst(contractor.companyName, club.businessName);
    const tradeName = pickFirst(contractor.tradeName, club.name);
    const document = pickFirst(contractor.document, club.document);

    const addressParts = [
        contractor.address,
        contractor.number,
        contractor.complement,
        contractor.neighborhood,
        contractor.city,
        contractor.state,
        contractor.zipcode,
    ].filter(Boolean);

    const planName = pickFirst(plan.name, contract?.contractData?.plan?.name);
    const planValue = pickFirst(contract?.value, plan.price, contract?.contractData?.plan?.price);
    const totalValue = pickFirst(contract?.totalValue, contract?.value);
    const discount = pickFirst(contract?.discount, 0);

    const lists = buildContractLists(contract);
    const signatories = contract?.contractData?.signatories || {};

    return {
        RAZAO_SOCIAL: companyName,
        NOME_FANTASIA: tradeName,
        CNPJ: document,
        NOME_CONTRATANTE: contractor.contractorName,
        EMAIL_CONTRATANTE: contractor.contractorEmail,
        TELEFONE_CONTRATANTE: contractor.contractorPhone,
        CARGO_CONTRATANTE: contractor.contractorPosition,
        DOCUMENTO_CONTRATANTE: contractor.contractorDocument,
        NACIONALIDADE_CONTRATANTE: contractor.contractorNationality,
        NOME_CONTRATADA: signatories.contracted?.name,
        EMAIL_CONTRATADA: signatories.contracted?.email,
        DOCUMENTO_CONTRATADA: signatories.contracted?.document,
        NOME_TESTEMUNHA_1: signatories.witness1?.name,
        EMAIL_TESTEMUNHA_1: signatories.witness1?.email,
        DOCUMENTO_TESTEMUNHA_1: signatories.witness1?.document,
        NOME_TESTEMUNHA_2: signatories.witness2?.name,
        EMAIL_TESTEMUNHA_2: signatories.witness2?.email,
        DOCUMENTO_TESTEMUNHA_2: signatories.witness2?.document,
        CEP: contractor.zipcode || club.zipcode,
        ENDERECO: contractor.address || club.street,
        NUMERO: contractor.number || club.number,
        COMPLEMENTO: contractor.complement || club.complement,
        BAIRRO: contractor.neighborhood || club.neighborhood,
        CIDADE: contractor.city || club.city,
        ESTADO: contractor.state || club.state,
        ENDERECO_COMPLETO: addressParts.join(', '),
        MESES_FIDELIDADE: fidelityMonths,
        VALOR_MULTA: formatCurrency(fidelityPenalty),
        NOME_PLANO: planName,
        VALOR_MENSALIDADE: formatCurrency(planValue),
        VALOR_TOTAL: formatCurrency(totalValue),
        VALOR_DESCONTO: formatCurrency(discount),
        NOME: tradeName || companyName,
        CPF: document,
        ...lists,
    };
}

function isTruthyPlaceholder(value) {
    if (value === undefined || value === null) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return String(value).trim() !== '';
}

/**
 * Substitui {{VAR}} e blocos opcionais {{#VAR}}...{{/VAR}}.
 * Bloco some se a variável for vazia/falsa.
 */
export function applyContractPlaceholders(html, contract) {
    if (!html) return '';

    const placeholders = buildContractPlaceholderMap(contract);

    let rendered = html;

    // Blocos condicionais (suporta aninhamento simples por repetição)
    for (let i = 0; i < 5; i += 1) {
        const next = rendered.replace(/\{\{#([A-Z0-9_]+)\}\}([\s\S]*?)\{\{\/\1\}\}/gi, (match, key, block) => {
            const value = placeholders[key.toUpperCase()];
            if (!isTruthyPlaceholder(value)) return '';
            return block;
        });
        if (next === rendered) break;
        rendered = next;
    }

    rendered = rendered.replace(/\{\{\s*([A-Z0-9_]+)\s*\}\}/gi, (match, key) => {
        const value = placeholders[key.toUpperCase()];
        if (value === undefined || value === null || String(value).trim() === '') {
            return match;
        }
        return String(value);
    });

    return rendered;
}

export function countPendingPlaceholders(html, contract) {
    if (!html) return 0;

    // Conta só após aplicar condicionais (blocos removidos não contam)
    const rendered = applyContractPlaceholders(html, contract);
    return (rendered.match(/\{\{\s*([A-Z0-9_]+)\s*\}\}/gi) || []).length;
}

export function hasContractPlaceholders(html) {
    return /\{\{\s*#?[A-Z0-9_]+\s*\}\}/i.test(html || '');
}

const PLACEHOLDER_MARK_CLASS = 'contract-placeholder-pending';

/** Conta tokens simples {{VAR}} (ignora {{#VAR}} / {{/VAR}}). */
export function countSimplePlaceholders(html) {
    if (!html) return 0;
    return (String(html).match(/\{\{\s*([A-Z0-9_]+)\s*\}\}/gi) || []).length;
}

/**
 * Destaca visualmente {{VAR}} restantes (só variáveis simples).
 * Wrappers são só para UI — remover com stripPlaceholderHighlights antes de salvar.
 */
export function highlightPendingPlaceholders(html) {
    if (!html) return '';
    const stripped = stripPlaceholderHighlights(html);
    return stripped.replace(/\{\{\s*([A-Z0-9_]+)\s*\}\}/gi, match => {
        return `<mark class="${PLACEHOLDER_MARK_CLASS}" data-contract-placeholder="1">${match}</mark>`;
    });
}

/** Remove marks de highlight de placeholders (não altera o token {{VAR}}). */
export function stripPlaceholderHighlights(html) {
    if (!html || typeof html !== 'string') return html || '';
    return html
        .replace(
            new RegExp(
                `<mark[^>]*class=["'][^"']*${PLACEHOLDER_MARK_CLASS}[^"']*["'][^>]*>([\\s\\S]*?)<\\/mark>`,
                'gi',
            ),
            '$1',
        )
        .replace(
            new RegExp(
                `<mark[^>]*data-contract-placeholder=["']1["'][^>]*>([\\s\\S]*?)<\\/mark>`,
                'gi',
            ),
            '$1',
        );
}

/**
 * Remove documento HTML completo / CSS global.
 * Se injetado no contentEditable, <style> com body/p vaza e quebra a UI da página.
 */
export function sanitizeContractHtmlForEditor(html) {
    if (!html || typeof html !== 'string') return '';

    let out = html;
    const bodyMatch = out.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) out = bodyMatch[1];

    out = out.replace(/<!DOCTYPE[\s\S]*?>/gi, '');
    out = out.replace(/<\/?html[^>]*>/gi, '');
    out = out.replace(/<head[\s\S]*?<\/head>/gi, '');
    out = out.replace(/<style[\s\S]*?<\/style>/gi, '');
    out = out.replace(/<\/?body[^>]*>/gi, '');

    return out.trim();
}

/**
 * Fonte canônica com {{VARS}} (legado / referência).
 * O processamento atual usa sempre o template base do servidor.
 */
export function resolveContractTextSource(contract, fallbackHtml = '') {
    const source = contract?.contractData?.contractTextSource;
    if (source && hasContractPlaceholders(source)) return source;

    const current = fallbackHtml || contract?.contractData?.contractText || '';
    if (hasContractPlaceholders(current)) return current;

    return source || '';
}
